export const BUSINESS_HOURS_TYPES = [
  'HOURS_NOT_AVAILABLE',
  'ALWAYS_OPEN',
  'SELECTED_HOURS',
] as const;
export type BusinessHoursType = (typeof BUSINESS_HOURS_TYPES)[number];

export const BUSINESS_DAYS = [
  'EVERYDAY',
  'WEEKDAYS',
  'WEEKENDS',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const;
export type BusinessDay = (typeof BUSINESS_DAYS)[number];

/** One persisted row. Both times `null` ⇒ open 24 hours that day. */
export interface BusinessHoursRowDTO {
  business_day?: BusinessDay | null;
  from_time?: string | null;
  to_time?: string | null;
}

type Weekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

const WEEK: Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const SHORT_LABEL: Record<Weekday, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

/** Group day-values expand to their weekdays; individual days map 1:1. */
const BUSINESS_DAY_TO_WEEKDAYS: Record<BusinessDay, Weekday[]> = {
  MONDAY: ['mon'],
  TUESDAY: ['tue'],
  WEDNESDAY: ['wed'],
  THURSDAY: ['thu'],
  FRIDAY: ['fri'],
  SATURDAY: ['sat'],
  SUNDAY: ['sun'],
  WEEKDAYS: ['mon', 'tue', 'wed', 'thu', 'fri'],
  WEEKENDS: ['sat', 'sun'],
  EVERYDAY: WEEK,
};

const dayIndex = (day: Weekday): number => WEEK.indexOf(day);

function sortDays(days: Weekday[]): Weekday[] {
  return [...days].sort((a, b) => dayIndex(a) - dayIndex(b));
}

function earliestDayIndex(days: Weekday[]): number {
  return days.length === 0 ? WEEK.length : Math.min(...days.map(dayIndex));
}

function to12Hour(time: string): string {
  const [hoursText = '0', minutes = '00'] = time.split(':');
  const hours24 = Number.parseInt(hoursText, 10);
  const period = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;
  return `${hours12}:${minutes} ${period}`;
}

/** Collapse contiguous weekdays into ranges, e.g. ['mon','tue','wed'] → 'Mon–Wed'. */
function formatDayRuns(days: Weekday[]): string {
  const runs = sortDays(days).reduce<Array<{ first: Weekday; last: Weekday }>>(
    (acc, day) => {
      const lastRun = acc[acc.length - 1];

      if (lastRun && dayIndex(day) === dayIndex(lastRun.last) + 1) {
        lastRun.last = day;
      } else {
        acc.push({ first: day, last: day });
      }

      return acc;
    },
    [],
  );

  return runs
    .map((run) =>
      run.first === run.last
        ? SHORT_LABEL[run.first]
        : `${SHORT_LABEL[run.first]}–${SHORT_LABEL[run.last]}`,
    )
    .join(', ');
}

interface DisplayRow {
  days: Weekday[];
  is24Hours: boolean;
  openTime: string | null;
  closeTime: string | null;
}

/** Merge rows sharing a time range into one line and expand group day-values. */
function groupRows(businessHours: BusinessHoursRowDTO[]): DisplayRow[] {
  const rowByKey = new Map<string, DisplayRow>();
  const rows: DisplayRow[] = [];

  businessHours.forEach((apiRow) => {
    const weekdays = apiRow.business_day
      ? BUSINESS_DAY_TO_WEEKDAYS[apiRow.business_day]
      : undefined;

    if (!weekdays) {
      return;
    }

    const fromTime = apiRow.from_time || null;
    const toTime = apiRow.to_time || null;
    const is24Hours = fromTime == null && toTime == null;

    if (!is24Hours && (fromTime == null || toTime == null)) {
      return;
    }

    const key = is24Hours ? '24H' : `${fromTime}-${toTime}`;
    let row = rowByKey.get(key);

    if (!row) {
      row = {
        days: [],
        is24Hours,
        openTime: is24Hours ? null : fromTime,
        closeTime: is24Hours ? null : toTime,
      };
      rowByKey.set(key, row);
      rows.push(row);
    }

    const target = row;
    weekdays.forEach((day) => {
      if (!target.days.includes(day)) {
        target.days.push(day);
      }
    });
  });

  return rows
    .map((row) => ({ ...row, days: sortDays(row.days) }))
    .sort(
      (left, right) =>
        earliestDayIndex(left.days) - earliestDayIndex(right.days),
    );
}

function formatRow(row: DisplayRow): string {
  const days = formatDayRuns(row.days);

  if (row.is24Hours) {
    return `${days} · Open 24 hours`;
  }

  if (row.openTime && row.closeTime) {
    return `${days} · ${to12Hour(row.openTime)} – ${to12Hour(row.closeTime)}`;
  }

  // Unreachable: `groupRows` drops rows without a complete range.
  return days;
}

/**
 * Formats business hours into read-only display lines.
 * `ALWAYS_OPEN` ⇒ a single "Open 24 hours"; `SELECTED_HOURS` ⇒ one line per
 * grouped schedule; anything else (incl. missing data) ⇒ no lines. Malformed
 * rows (unknown day, or only one of the two times) are skipped.
 * e.g. WEEKDAYS 08:00–17:00 + SATURDAY 24h → ['Mon–Fri · 8:00 AM – 5:00 PM', 'Sat · Open 24 hours'].
 */
export function formatBusinessHours(
  businessHoursType: BusinessHoursType | null | undefined,
  businessHours: BusinessHoursRowDTO[] | null | undefined,
): string[] {
  if (businessHoursType === 'ALWAYS_OPEN') {
    return ['Open 24 hours'];
  }

  if (businessHoursType !== 'SELECTED_HOURS' || !businessHours?.length) {
    return [];
  }

  return groupRows(businessHours).map(formatRow);
}
